/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/ui/DateRangePicker.tsx
 * PURPOSE: Bộ chọn khoảng ngày dùng chung (lịch kép — chọn ngày bắt đầu rồi
 *          lia chuột chọn ngày kết thúc) thay thế các "Date Selector" cuộn
 *          ngang rời rạc trên Web MANAGER (VĐ1 Phân ca, VĐ2 Chấm công, VĐ5
 *          Giao việc — SDLC 6.5.0 batch 2026-08-16)
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-08-16
 * COMPLIANCE: SDLC 6.5.0 + NQH Brand Design System + Zero Mock Tolerance
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 *
 * @business-context
 * - Domain: Manager — chọn khoảng ngày (Phân ca hàng loạt, filter Chấm công,
 *   filter Giao việc)
 * - Impact: UX — 1 component dùng chung thay vì mỗi màn tự làm 1 kiểu chọn
 *   ngày khác nhau
 * - Stakeholders: Branch Managers
 *
 * @technical-architecture
 * - Layer: Presentation Layer (reusable UI component)
 * - Pattern: Controlled component (value/onChange), react-day-picker v9
 *   mode="range" (đã có sẵn trong deps — dist/esm/UI.d.ts) + popover tự viết
 *   (không phụ thuộc Radix/shadcn — nhất quán style thuần CSS-module của
 *   các trang Manager hiện có, xem ManagerSchedulePage.module.css)
 * - Dependencies: react-day-picker ^9.8.1 (đã có trong package.json)
 *
 * @cross-references
 * - Consumers: ManagerSchedulePage.tsx (VĐ1 popup Phân ca), ManagerAttendancePage.tsx
 *   (VĐ2 filter), ManagerTasksPage.tsx (VĐ5 Giao việc)
 * ===============================================================================
 */

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { DayPicker, type DateRange } from "react-day-picker";
// v9 export map: "react-day-picker/style.css" -> src/style.css (base reset;
// giao diện thật do classNames map bên dưới ghi đè bằng token NQH).
import "react-day-picker/style.css";
import styles from "./DateRangePicker.module.css";

const CalendarIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={18} height={18} aria-hidden="true">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path strokeLinecap="round" d="M3 10h18M8 3v4M16 3v4" />
  </svg>
);

export interface DateRangeValue {
  start: string | null; // YYYY-MM-DD
  end: string | null; // YYYY-MM-DD
}

interface Preset {
  label: string;
  range: () => DateRangeValue;
}

const pad2 = (n: number) => String(n).padStart(2, "0");
const toISO = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const fromISO = (s: string): Date => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const fmtVI = (s: string) => {
  const d = fromISO(s);
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`;
};

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const DEFAULT_PRESETS: Preset[] = [
  {
    label: "Hôm nay",
    range: () => {
      const t = toISO(startOfToday());
      return { start: t, end: t };
    },
  },
  {
    label: "7 ngày tới",
    range: () => {
      const s = startOfToday();
      const e = new Date(s);
      e.setDate(e.getDate() + 6);
      return { start: toISO(s), end: toISO(e) };
    },
  },
  {
    label: "30 ngày tới",
    range: () => {
      const s = startOfToday();
      const e = new Date(s);
      e.setDate(e.getDate() + 29);
      return { start: toISO(s), end: toISO(e) };
    },
  },
];

export interface DateRangePickerProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  placeholder?: string;
  /** Ẩn dải preset nhanh (Hôm nay/7 ngày/30 ngày) — mặc định hiện */
  hidePresets?: boolean;
  /** Số ngày tối đa cho phép chọn (VĐ1: 62 ngày/lần khớp giới hạn backend) */
  maxSpanDays?: number;
  disabled?: boolean;
  id?: string;
}

/**
 * Bộ chọn khoảng ngày — bấm mở popover lịch kép, chọn ngày bắt đầu rồi lia
 * chuột chọn ngày kết thúc (react-day-picker mode="range" xử lý sẵn hành vi
 * này: click 1 → range={from,to:undefined}; click 2 → hoàn tất; click 3 →
 * bắt đầu range mới).
 */
/** Neo popover theo trigger, tự né mép màn hình — cùng pattern anchorTo() đã
 * dùng ở RangeAssignPopover.tsx/ShiftCellPopover.tsx. BUG THẬT đã vá
 * (2026-08-16): trước đây .popover chỉ `position: absolute` KHÔNG có
 * top/left/right nào — khi trigger nằm gần mép phải màn hình (vd toolbar
 * "Chấm công" đẩy phải), popover 2-tháng khá rộng tràn ra ngoài/đè lên nút
 * kế bên vì không có gì tính toán vị trí cả. */
const anchorTo = (
  anchor: { left: number; top: number; bottom: number; right: number },
  width: number,
  height: number,
) => {
  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;
  // Mặc định căn theo mép PHẢI trigger (mở về bên trái) — khớp cách dùng phổ
  // biến nhất (toolbar đẩy phải); vẫn kẹp lại nếu tràn mép trái/phải.
  let left = anchor.right - width;
  const maxLeft = vw - width - 8;
  if (left > maxLeft) left = maxLeft;
  if (left < 8) left = 8;
  let top = anchor.bottom + 6;
  if (top + height > vh - 8) {
    const above = anchor.top - height - 6;
    top = above >= 8 ? above : Math.max(8, vh - height - 8);
  }
  return { top, left };
};

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  placeholder = "Chọn khoảng ngày",
  hidePresets = false,
  maxSpanDays,
  disabled = false,
  id,
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRangeValue>(value);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: -9999, left: -9999 });

  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  useLayoutEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    const popover = popoverRef.current;
    if (!trigger || !popover) return;
    const anchor = trigger.getBoundingClientRect();
    const w = popover.offsetWidth || 640;
    const h = popover.offsetHeight || 420;
    setPos(anchorTo(anchor, w, h));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draft.start, draft.end]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const selectedRange: DateRange | undefined = useMemo(() => {
    if (!draft.start) return undefined;
    return {
      from: fromISO(draft.start),
      to: draft.end ? fromISO(draft.end) : undefined,
    };
  }, [draft]);

  const label = useMemo(() => {
    if (!value.start) return placeholder;
    if (!value.end || value.end === value.start) return fmtVI(value.start);
    return `${fmtVI(value.start)} – ${fmtVI(value.end)}`;
  }, [value, placeholder]);

  const handleSelect = (range: DateRange | undefined) => {
    if (!range?.from) {
      setDraft({ start: null, end: null });
      return;
    }
    let start = toISO(range.from);
    let end = range.to ? toISO(range.to) : null;
    if (maxSpanDays && end) {
      const spanMs = fromISO(end).getTime() - fromISO(start).getTime();
      const spanDays = Math.round(spanMs / 86400000) + 1;
      if (spanDays > maxSpanDays) {
        const capped = new Date(fromISO(start));
        capped.setDate(capped.getDate() + maxSpanDays - 1);
        end = toISO(capped);
      }
    }
    setDraft({ start, end });
  };

  const applyPreset = (preset: Preset) => {
    setDraft(preset.range());
  };

  const handleApply = () => {
    if (draft.start && !draft.end) {
      onChange({ start: draft.start, end: draft.start });
    } else {
      onChange(draft);
    }
    setOpen(false);
  };

  const handleClear = () => {
    setDraft({ start: null, end: null });
    onChange({ start: null, end: null });
    setOpen(false);
  };

  return (
    <div className={styles.wrap} ref={containerRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        className={styles.trigger}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className={styles.triggerIcon}>
          <CalendarIcon />
        </span>
        <span className={value.start ? undefined : styles.triggerPlaceholder}>{label}</span>
      </button>

      {open && (
        <div
          ref={popoverRef}
          className={styles.popover}
          role="dialog"
          aria-label="Chọn khoảng ngày"
          style={{ top: pos.top, left: pos.left }}
        >
          {!hidePresets && (
            <div className={styles.presets}>
              {DEFAULT_PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  className={styles.presetBtn}
                  onClick={() => applyPreset(p)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}
          <div className={styles.months}>
            <DayPicker
              mode="range"
              numberOfMonths={2}
              selected={selectedRange}
              onSelect={handleSelect}
              weekStartsOn={1}
              locale={undefined}
              formatters={{
                formatCaption: (date) =>
                  `Tháng ${date.getMonth() + 1}/${date.getFullYear()}`,
                formatWeekdayName: (date) =>
                  ["CN", "T2", "T3", "T4", "T5", "T6", "T7"][date.getDay()],
              }}
              classNames={{
                root: styles.rdpRoot,
                months: styles.rdpMonths,
                month_caption: styles.rdpMonthCaption,
                day: styles.rdpDay,
                day_button: styles.rdpDayButton,
                outside: styles.rdpOutside,
                today: styles.rdpToday,
                selected: styles.rdpSelected,
                range_start: styles.rdpRangeStart,
                range_middle: styles.rdpRangeMiddle,
                range_end: styles.rdpRangeEnd,
              }}
            />
          </div>
          <div className={styles.footer}>
            <span className={styles.footerLabel}>
              {draft.start
                ? draft.end && draft.end !== draft.start
                  ? `${fmtVI(draft.start)} – ${fmtVI(draft.end)}`
                  : fmtVI(draft.start)
                : "Chưa chọn ngày"}
            </span>
            <div className={styles.footerActions}>
              <button type="button" className={styles.presetBtn} onClick={handleClear}>
                Xoá
              </button>
              <button
                type="button"
                className={styles.presetBtn}
                onClick={handleApply}
                disabled={!draft.start}
              >
                Áp dụng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
